// ============================================================
// BuildPilot unified search.
//
// Searches across the full content index (glossary terms,
// builders, suburbs, estates, case studies, help articles,
// suppliers, hubs, cornerstone pages - see contentIndex.js)
// and the curated intent rules. Each result carries a `kind`
// so the UI can render it appropriately:
//
//   kind: 'glossary'         → term + definition snippet
//   kind: 'builder'          → builder name + type/HQ
//   kind: 'suburb'           → suburb name + region
//   kind: 'estate'           → estate name + location
//   kind: 'case-study'       → client name + suburb/project
//   kind: 'help'             → help title + snippet
//   kind: 'supplier'         → supplier name
//   kind: 'supplier-category'→ category aggregation
//   kind: 'page'             → hub or cornerstone page
//
// Scoring weights are intentionally simple and tuned to
// surface the most direct match first. Stop-words are
// filtered so noise like "what is …" doesn't bias scoring.
// ============================================================

import { getContentIndex } from './contentIndex';
import { getLiveSuggestions, intentRules, fallbackRoute } from './intentRouting';
import { parseCompoundIntent } from './compoundIntent';

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'do', 'does', 'did', 'has', 'have', 'had', 'in', 'on', 'at', 'to',
  'for', 'of', 'with', 'by', 'from', 'as', 'or', 'and', 'but', 'if',
  'so', 'it', 'its', 'this', 'that', 'these', 'those', 'i', 'we', 'you',
  'my', 'your', 'our', 'me', 'us', 'what', 'why', 'how', 'when', 'where',
  'who', 'which', 'can', 'could', 'should', 'would', 'will', 'shall',
  'mean', 'means', 'meaning', 'tell', 'show',
]);

const tokenize = (s) =>
  (s || '')
    .toLowerCase()
    .split(/[^a-z0-9$]+/)
    .filter((t) => t.length >= 2);

const tokenizeMeaningful = (s) =>
  tokenize(s).filter((t) => !STOP_WORDS.has(t));

// ─── Typo-tolerance ──────────────────────────────────────────
//
// One-edit Levenshtein-distance check (insertion / deletion /
// substitution / transposition). We cap it at distance 1 so we
// only catch genuine typos without producing weird matches.
//
// Used to fuzzy-match query tokens against title tokens so that
// "metircon", "munno parra" or "kockdown" still surface results.
const fuzzyEqual = (a, b) => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  // Substitution / transposition for equal lengths
  if (a.length === b.length) {
    let diffs = 0;
    let firstDiff = -1;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        diffs += 1;
        if (diffs === 1) firstDiff = i;
        if (diffs > 2) return false;
      }
    }
    if (diffs <= 1) return true;
    // Two diffs only OK if they are adjacent transposition
    if (diffs === 2 && firstDiff >= 0 && firstDiff < a.length - 1) {
      return a[firstDiff] === b[firstDiff + 1] && a[firstDiff + 1] === b[firstDiff];
    }
    return false;
  }
  // Single insertion / deletion (lengths differ by 1)
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) { i += 1; j += 1; }
    else { edits += 1; j += 1; if (edits > 1) return false; }
  }
  return true;
};

// Build a snippet of the description preferring the section
// surrounding the matched query so the user sees the most
// relevant ~160 chars instead of always the start.
const buildSnippet = (text, query, maxLen = 180) => {
  if (!text) return '';
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLen) return clean;

  const q = (query || '').toLowerCase().trim();
  if (q && q.length >= 3) {
    const idx = clean.toLowerCase().indexOf(q);
    if (idx >= 0) {
      const start = Math.max(0, idx - 50);
      const end = Math.min(clean.length, start + maxLen);
      const slice = clean.slice(start, end).trim();
      return (start > 0 ? '… ' : '') + slice + (end < clean.length ? '…' : '');
    }
  }
  return clean.slice(0, maxLen).trim() + '…';
};

// ─── Per-kind weight (so a suburb match doesn't drown a glossary match) ──
const KIND_WEIGHT = {
  glossary: 1.0,
  builder: 1.0,
  suburb: 1.0,
  estate: 0.95,
  'case-study': 0.85,
  help: 0.9,
  supplier: 1.0,
  'supplier-category': 0.9,
  page: 0.85,
  // Insider "What the pros see" reading guides. Ranked a touch above
  // plain pages so a pros pick surfaces for queries like "plan",
  // "quote", "inspection" or "display".
  pros: 1.1,
  // Workspace tools rank slightly above content hits when title/keyword
  // matches because they're an action ("track costs", "compare quotes")
  // rather than an article. Still scored against the same MIN_SCORE
  // threshold so they don't crowd out content for generic queries.
  'workspace-tool': 1.15,
};

// ─── Score a single content item against the query ─────────
//
// Layered weights:
//   - Title === query                     → 220
//   - Title startsWith / contains query   → 90 / 60
//   - Title token match (per token)       → 25
//   - Keyword phrase contains query       → 30
//   - Keyword phrase token match          → 6 per kw token
//   - Snippet contains query              → 14
//   - Snippet token match (capped)        → 4 per token (max 20)
const scoreItem = (item, q, tokens) => {
  const titleLower = (item.title || '').toLowerCase();
  const titleTokens = tokenize(item.title);
  const subtitleLower = (item.subtitle || '').toLowerCase();
  const snippetLower = (item.snippet || '').toLowerCase();
  const keywordsLower = (item.keywords || '').toLowerCase();
  const keywordTokens = tokenize(item.keywords);

  let score = 0;

  // 1. Title match (strongest)
  if (titleLower === q) score += 220;
  else if (titleLower.startsWith(q + ' ') || titleLower.startsWith(q)) score += 90;
  else if (titleLower.includes(q)) score += 60;

  // 2. Title token match (fuzzy, so typos like "metircon" still hit)
  let titleTokenHits = 0;
  for (const t of tokens) {
    if (titleTokens.includes(t)) { score += 25; titleTokenHits += 1; }
    else if (titleLower.includes(t)) score += 8;
    else if (t.length >= 4 && titleTokens.some((tt) => tt.length >= 4 && fuzzyEqual(t, tt))) score += 22;
  }
  // Every meaningful token landed in the title: the user is naming this
  // page. Lift it above pinned catalog rows that only fuzzy-matched.
  if (tokens.length >= 2 && titleTokenHits === tokens.length) score += 70;

  // 3. Subtitle (region / category) - gentle boost only
  for (const t of tokens) {
    if (subtitleLower.includes(t)) score += 4;
  }

  // 4. Keyword phrase match - these are curated tags
  if (keywordsLower) {
    if (keywordsLower.includes(q)) score += 30;
    let kwTokenHits = 0;
    for (const t of tokens) {
      if (keywordTokens.includes(t)) kwTokenHits += 1;
    }
    score += Math.min(kwTokenHits * 6, 24);
  }

  // 5. Snippet match (last-resort signal)
  if (q.length >= 4 && snippetLower.includes(q)) score += 14;
  let snippetHits = 0;
  for (const t of tokens) {
    if (snippetLower.includes(t)) snippetHits += 1;
  }
  score += Math.min(snippetHits * 4, 20);

  // Apply per-kind weight
  score *= KIND_WEIGHT[item.kind] ?? 1.0;

  return score;
};

// ─── Public API ──────────────────────────────────────────────

/**
 * Returns a ranked, mixed result set. Each result has a `kind`.
 *
 * @param {string} rawQuery
 * @param {{ limit?: number }} options
 */
export function searchAll(rawQuery, { limit = 8 } = {}) {
  const q = (rawQuery || '').toLowerCase().trim();
  if (!q) return [];

  const tokens = tokenizeMeaningful(q);

  // If the query is all stop-words, fall back to intent suggestions
  // only so the dropdown still surfaces something useful.
  if (tokens.length === 0) {
    return getLiveSuggestions(rawQuery, limit).map((s) => ({
      kind: 'page',
      score: 35,
      title: s.label,
      to: s.to,
    }));
  }

  // 0. Compound intent - [category + location] queries get a synthesised
  //    answer card pinned to the top of the dropdown so the user sees a
  //    direct "Builders in Munno Para" or "Verandahs in Greenacres"
  //    result before the regular index hits.
  const compound = parseCompoundIntent(rawQuery);

  // 1. Score every content item.
  const index = getContentIndex();
  const contentHits = [];
  const MIN_SCORE = 22;
  for (const item of index) {
    const score = scoreItem(item, q, tokens);
    if (score >= MIN_SCORE) {
      contentHits.push({
        ...item,
        score,
        snippet: buildSnippet(item.snippet, q),
      });
    }
  }
  contentHits.sort((a, b) => b.score - a.score);

  // 2. Pull intent-rule hits in. Dedupe against content-hit destinations
  //    so we don't surface the same URL twice.
  const contentDests = new Set(contentHits.map((h) => h.to));
  const intentHits = getLiveSuggestions(rawQuery, limit)
    .filter((s) => !contentDests.has(s.to))
    .map((s) => ({
      kind: 'page',
      score: s.label.toLowerCase() === q ? 70 : 35,
      title: s.label,
      to: s.to,
    }));

  // 3. Merge + final sort + cap to limit
  //    Compound result is pinned to the top (score 1000) and de-duped
  //    against any same-route entry.
  const allHits = [];
  const finalSeen = new Set();
  if (compound) {
    allHits.push(compound);
    finalSeen.add(compound.to);
  }
  for (const h of [...contentHits, ...intentHits]) {
    if (finalSeen.has(h.to)) continue;
    finalSeen.add(h.to);
    allHits.push(h);
  }
  allHits.sort((a, b) => b.score - a.score);
  return allHits.slice(0, limit);
}

/**
 * Resolve a query to a single best destination on Enter / Submit.
 */
export function resolveBestRoute(rawQuery) {
  const results = searchAll(rawQuery, { limit: 1 });
  if (results.length > 0) {
    return { to: results[0].to, label: results[0].title };
  }
  return { to: fallbackRoute.to, label: fallbackRoute.label };
}

export { intentRules };
